import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { RouteComponentProps } from "@reach/router";
import {
  addIssuer,
  getAllIssuers,
  getIssuerWithUsername,
  updateIssuer,
  updateIssuerSecret
} from "../utils/helpers/data/issuers";
import Table, { Column, Pagination } from "../components/table/Table";
import ContextMenu from "../components/context-menu/ContextMenu";
import Modal from "../components/modal/Modal";
import Button from "../components/button/Button";
import Input, { InputValidation } from "../components/input/Input";
import Select from "../components/select/Select";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";
import { Issuer, IssuerCreationData } from "../utils/helpers/types";
import styles from "./issuers.module.scss";
import SecretDisplay from "../components/secret-display/SecretDisplay";
import ToolTip from "../components/ToolTip/ToolTip";
import { resources } from "../utils/resources";
import { BooleanFilter, PartialLoose } from "../utils/helpers/types-helpers";
import {
  alphaNumericValidation,
  alphaNumericValidationWithDot,
  isBase64,
  isValidDomain
} from "../utils/helpers/validation-helpers";
import UserContext from "../context/user-context";

const roleRender: (value: string) => JSX.Element = value => (
  <span className="tag smaller">{value.replace("_", " ").toUpperCase()}</span>
);

const IssuersPage: FunctionComponent<RouteComponentProps> = () => {
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [recordOnEdit, setRecordOnEdit] = useState<Issuer | null>(null);
  const [recordOnView, setRecordOnView] = useState<Issuer | null>(null);
  const [dataSource, setDataSource] = useState<Issuer[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>(
    initialPaginationData
  );
  const [searchStr, setSearchStr] = useState("");
  const [searching, setSearching] = useState(false);

  const displayAddEditModal: (record: Issuer | null) => void = record => {
    setRecordOnEdit(record);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setRecordOnEdit(null);
    setShowAddEditModal(false);
  };

  const { notify } = useContext(LayoutContext);

  const fetchIssuers: (
    pageNumber?: number,
    pageSize?: number
  ) => Promise<void> = async (
    pageNumber = 1,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const response = await getAllIssuers({
      pageNumber,
      pageSize
    });
    setLoading(false);
    if (response.error) {
      notify("error", response.message as string);
      return;
    }
    const { count = 0, issuers } = response.data || {};
    setPagination({ pageSize, pageNumber, total: count });
    setDataSource(issuers || []);
    if (recordOnView && issuers) {
      const newRecord = issuers.find(
        ({ issuerId }) => recordOnView.issuerId === issuerId
      );
      if (newRecord) {
        setRecordOnView(newRecord);
      }
    }
  };

  useEffect(() => {
    fetchIssuers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextRender: (value: string, record: any) => JSX.Element = (
    _,
    record
  ) => (
    <ContextMenu
      options={[
        {
          label: "View",
          onClick: () => setRecordOnView(record)
        },
        {
          label: "Edit",
          onClick: () => displayAddEditModal(record)
        }
      ]}
    />
  );

  const columns: Column[] = [
    {
      title: "Issuer ID",
      dataIndex: "issuerId",
      key: "issuerId"
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username"
    },
    {
      title: "User Role",
      dataIndex: "userRole",
      key: "userRole",
      render: roleRender
    },
    {
      title: "Organization",
      dataIndex: "organizationName",
      key: "organizationName"
    },
    {
      title: "",
      dataIndex: "menu",
      key: "menu",
      render: contextRender
    }
  ];

  const updatePageSize = (pageSize: number) => {
    localStorage.setItem("ITG_SAVED_PAGE_SIZE", String(pageSize));
    fetchIssuers(1, pageSize);
  };

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    setSearching(true);
    setLoading(true);
    const { error, message, data } = await getIssuerWithUsername(searchStr);
    setSearching(false);
    setLoading(false);
    if (error && !/not\s*found/.test(message || "")) {
      notify("error", message as string);
      return;
    }
    const dataSource = [data].filter(BooleanFilter);
    setDataSource(dataSource);
    setPagination({
      pageNumber: 1,
      total: dataSource.length ? 1 : 0,
      pageSize: pagination.pageSize
    });
  };

  const handleSearchReset = () => {
    setSearchStr("");
    fetchIssuers(pagination.pageNumber);
  };

  useEffect(() => {
    if (!searchStr.length) {
      handleSearchReset();
    }
  }, [searchStr]);

  return (
    <main className="content-layout">
      <h1 className="title vertical-margin spaced thin">Issuers</h1>
      <div className="vertical-margin spaced flex between">
        <form className="flex spaced center-align" onSubmit={handleSearch}>
          <Input
            id="issuerSearchInput"
            value={searchStr}
            onChange={setSearchStr}
            placeholder="Find issuer by username"
            onInputClear={handleSearchReset}
          />
          <Button
            buttonType="submit"
            loading={searching}
            id="issuerSearchButton"
          >
            Find
          </Button>
        </form>
        <Button id="newissuerButton" onClick={() => displayAddEditModal(null)}>
          New Issuer
        </Button>
      </div>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchIssuers}
        loading={loading}
        onPageSizeChange={updatePageSize}
        emptyText="No Issuers found"
      />
      <AddEditIssuerModal
        visible={showAddEditModal}
        record={recordOnEdit}
        cancel={dismissAddEditModal}
        refresh={(stayOnPage: boolean) =>
          fetchIssuers(stayOnPage ? pagination.pageNumber : 1)
        }
      />
      <ViewIssuerModal
        record={recordOnView}
        cancel={() => setRecordOnView(null)}
        refresh={() => fetchIssuers(pagination.pageNumber)}
      />
    </main>
  );
};

interface AddEditModalProps {
  visible: boolean;
  cancel: () => void;
  record: Issuer | null;
  refresh: (stayOnPage: boolean) => void;
}

const initialFormData: IssuerCreationData = {
  username: "",
  organizationName: "",
  userRole: "service_manager",
  hostDomain: "",
  apiPath: "",
  basicAuthToken: "",
  domainCode: ""
};

const validations: PartialLoose<IssuerCreationData, InputValidation> = {
  username: {
    maxLength: 32,
    transform: alphaNumericValidationWithDot,
    required: true
  },
  organizationName: {
    maxLength: 32,
    transform: alphaNumericValidation,
    required: true
  }
};

export const AddEditIssuerModal: (props: AddEditModalProps) => JSX.Element =
  props => {
    const { visible, cancel, record, refresh } = props;

    const { userType } = useContext(UserContext) || {};

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);

    const handleChange = (key: keyof IssuerCreationData, value: any) => {
      setFormData({ ...formData, [key]: value });
    };

    const { notify } = useContext(LayoutContext);

    const handleSave = async (e: FormEvent) => {
      e.preventDefault();
      if (!formData.userRole) {
        notify("error", "Please select a user role");
        return;
      }
      if (formData.basicAuthToken && !isBase64(formData.basicAuthToken)) {
        notify(
          "error",
          "Please enter valid Base64 string for 'Basic Auth token' or leave the field empty"
        );
        return;
      }
      if (formData.hostDomain && !isValidDomain(formData.hostDomain)) {
        notify(
          "error",
          "Please enter valid domain for 'Host Domain' or leave the field empty"
        );
        return;
      }
      setLoading(true);
      const response = await (record
        ? updateIssuer({ id: record.id, ...formData })
        : addIssuer(formData));
      setLoading(false);
      if (response.error) {
        notify("error", response.message as string);
        return;
      }
      notify("success", `Issuer ${record ? "updated" : "added"} successfully`);
      cancel();
      refresh(Boolean(record));
    };

    useEffect(() => {
      const newData = record
        ? ((
            Object.keys(initialFormData) as (keyof IssuerCreationData)[]
          ).reduce(
            (map, key) => ({ ...map, [key]: record[key] }),
            {}
          ) as IssuerCreationData)
        : initialFormData;
      setFormData(newData);
    }, [record, visible]);

    return (
      <Modal visible={visible} cancel={cancel}>
        <h1 className="title thin vertical-margin spaced">
          {record ? "Edit" : "New"} Issuer
        </h1>
        <form onSubmit={handleSave} id="newIssuerForm">
          <div className="input-group">
            <span className="question">
              <span className="required">Username</span>
              <ToolTip
                toolTip="Username / clientId of the issuer when created on ISW core"
                anchor={
                  <img
                    alt="info tooltip"
                    src={resources.icons.INFO_CIRCLE_SOLID}
                    className="generic-icon small"
                  />
                }
              />
            </span>
            <Input
              value={formData.username}
              onChange={value => handleChange("username", value)}
              placeholder="Enter username"
              inputValidation={validations.username}
              disabled={userType === "ISSUER"}
              id="issuerUsername"
              name="issuerUsername"
            />
          </div>

          <div className="input-group">
            <span className="question">
              <span className="required">Organization</span>
            </span>
            <Input
              value={formData.organizationName}
              onChange={value => handleChange("organizationName", value)}
              placeholder="Enter organization name"
              inputValidation={validations.organizationName}
              id="issuerOrg"
              name="issuerOrg"
            />
          </div>

          <div className="input-group">
            <span className="question">User Role</span>
            <Select
              value={formData.userRole}
              onSelect={value => handleChange("userRole", value)}
              options={[{ label: "SERVICE MANAGER", value: "service_manager" }]}
              placeholder="Select user role"
              theme="light"
              responsive
              id="issuerRole"
            />
          </div>

          <div className="input-group">
            <span className="question">
              Host Domain
              <ToolTip
                toolTip="Host domain for issuer's card enrolment platform"
                anchor={
                  <img
                    alt="info tooltip"
                    src={resources.icons.INFO_CIRCLE_SOLID}
                    className="generic-icon small"
                  />
                }
              />
            </span>
            <Input
              value={formData.hostDomain}
              onChange={value => handleChange("hostDomain", value)}
              placeholder="Eg, www.lichess.org"
              id="issuerDomain"
              name="issuerDomain"
            />
          </div>

          <div className="input-group">
            <span className="question">
              API Path
              <ToolTip
                toolTip="Api path to issuer's card enrolment endpoints"
                anchor={
                  <img
                    alt="info tooltip"
                    src={resources.icons.INFO_CIRCLE_SOLID}
                    className="generic-icon small"
                  />
                }
              />
            </span>
            <Input
              value={formData.apiPath}
              onChange={value => handleChange("apiPath", value)}
              placeholder="Eg, /v2/user/options"
              id="issuerAPIPath"
              name="issuerAPIPath"
            />
          </div>

          <div className="input-group">
            <span className="question">Basic Auth token</span>
            <Input
              value={formData.basicAuthToken}
              onChange={value => handleChange("basicAuthToken", value)}
              placeholder="Enter auth token"
              id="issuerAuthToken"
              name="issuerAuthToken"
            />
          </div>

          <div className="input-group">
            <span className="question">Domain Code</span>
            <Input
              value={formData.domainCode}
              onChange={value => handleChange("domainCode", value)}
              placeholder="Enter domain code"
              id="issuerDomainCode"
              name="issuerDomainCode"
            />
          </div>

          <br />
          <div className="flex spaced end vertical-margin">
            <Button
              type="accent"
              buttonType="button"
              onClick={cancel}
              id="newIssuerCancel"
            >
              Cancel
            </Button>
            <Button buttonType="submit" loading={loading} id="newIssuerSubmit">
              {record ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>
    );
  };

interface ViewModalProps {
  record: Issuer | null;
  cancel: () => void;
  refresh: () => void;
}
const ViewIssuerModal: (props: ViewModalProps) => JSX.Element = ({
  record,
  cancel,
  refresh
}) => {
  const { confirm, notify } = useContext(LayoutContext);
  const [regeneratingSecret, setRegeneratingSecret] = useState(false);

  const updateSecret = async () => {
    const response = await updateIssuerSecret(record?.issuerId as string);
    if (response.error) {
      notify("error", `Unable to regenerate secret: ${response.message}`);
      return;
    }
    notify("success", "Secret generated successfully");
    refresh();
    setRegeneratingSecret(false);
  };

  const handleSecretRegeneration = () => {
    setRegeneratingSecret(true);
    confirm({
      title: `Are you sure you want to regenerate secret key for "${record?.username}"?`,
      body: "Current secret key would no longer be valid",
      onOk: updateSecret,
      onCancel: () => setRegeneratingSecret(false)
    });
  };

  return (
    <Modal
      visible={Boolean(record)}
      cancel={cancel}
      ignoreBackdropClicks={regeneratingSecret}
    >
      <div className={styles["issuer-view-wrapper"]}>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Row ID:</strong>
          <span className={styles.value}>{record?.id}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Issuer ID:</strong>
          <span className={styles.value}>{record?.issuerId}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Username:</strong>
          <span className={styles.value}>{record?.username}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>User Role:</strong>
          <span className={styles.value}>
            {roleRender(record?.userRole || "")}
          </span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Organization:</strong>
          <span className={styles.value}>{record?.organizationName}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Host Domain:</strong>
          <span className={styles.value}>{record?.hostDomain}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>API Path:</strong>
          <span className={styles.value}>{record?.apiPath}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Domain Code:</strong>
          <span className={styles.value}>{record?.domainCode}</span>
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Auth Token:</strong>
          {record && (
            <SecretDisplay
              value={record?.basicAuthToken || ""}
              className={styles.value}
            />
          )}
        </div>
        <div className={styles["key-value"]}>
          <strong className={styles.key}>Secret Key:</strong>
          {record && (
            <SecretDisplay
              value={record?.secretKey || ""}
              className={styles.value}
            />
          )}
        </div>

        <br />
        <Button
          danger
          type="accent"
          onClick={handleSecretRegeneration}
          id="regenerateSecretButton"
        >
          Regenerate Secret
        </Button>
      </div>
    </Modal>
  );
};

export default IssuersPage;
